import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const QA = [
  {
    q: "What services does Dataviqu offer?",
    a: "We work across three practices: Tech & Intelligence (AI, ML, data engineering, full-stack, cloud), Finance & Business Intelligence (financial analysis, reporting, forecasting, strategic planning), and Graphics & Design (brand identity, logos, UI/UX, social media). You can engage one practice or all three together.",
  },
  {
    q: "Do you provide custom AI and Machine Learning solutions?",
    a: "Yes. Every AI/ML engagement is custom-built. We start with your data and business goals, then design and deploy models, agents, and pipelines that integrate cleanly with your existing stack and team workflow.",
  },
  {
    q: "Can you help with business intelligence and financial reporting?",
    a: "Absolutely. We build dashboards, reporting systems and financial models — from board-ready performance reports to forecasting tools that finance teams actually want to use. Our work is grounded in real accounting and business reality, not just charts.",
  },
  {
    q: "Do you offer branding and design services?",
    a: "Yes. We design brand identity systems, logos, UI/UX for web and product, and social media design. Our design team is closely paired with our engineering team — so the brand you launch is the brand that ships in your product.",
  },
  {
    q: "How does the project process work?",
    a: "A typical engagement runs in four phases: (1) Discovery & scoping, (2) Strategy & blueprint, (3) Build & iterate with weekly demos, (4) Launch + handover. You get a dedicated point of contact, transparent milestones, and shared access to all work-in-progress.",
  },
  {
    q: "How can I get started with Dataviqu?",
    a: "Send us a project brief through the Contact form below. You'll receive a verification email, then a reply within 1–2 business days with proposed next steps, timelines and a free 30-minute scoping call.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-28 md:py-36 border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-400" data-testid="faq-overline">
            [ Frequently Asked ]
          </div>
          <h2
            className="mt-5 font-display text-4xl md:text-5xl font-medium tracking-tight"
            data-testid="faq-heading"
          >
            Questions, answered.
          </h2>
          <p className="mt-4 text-zinc-400 text-base md:text-lg">
            Quick reads on how we work, who we work with, and how to get started.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-14"
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {QA.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="border-b border-white/10 border-t-0"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="text-left py-6 hover:no-underline group"
                  data-testid={`faq-trigger-${i}`}
                >
                  <span className="flex items-baseline gap-4 flex-1">
                    <span className="font-mono text-xs text-zinc-500 shrink-0">0{i + 1}</span>
                    <span className="font-display text-lg md:text-xl font-light text-white group-hover:text-blue-400 transition-colors">
                      {item.q}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  className="pl-12 pb-6 text-zinc-400 text-base leading-relaxed"
                  data-testid={`faq-content-${i}`}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
